import React, { useState, useCallback } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  OpenInNew,
  Download,
} from '@mui/icons-material';

export default function PdfViewer({ 
  fileUrl, 
  fileName = 'document.pdf',
  maxWidth = 420,
  minHeight = 320,
  showNavigation = true,
  clickToOpen = true 
}) {
  const [totalPages, setTotalPages] = useState(0);
  const [openInNewTab, setOpenInNewTab] = useState(false);

  const handleDocumentLoadSuccess = useCallback((pdf) => {
    setTotalPages(pdf.numPages);
  }, []);

  const handlePdfClick = () => {
    if (clickToOpen) {
      setOpenInNewTab(true);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(fileUrl, '_blank');
    setOpenInNewTab(false);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: maxWidth,
          height: 500,
          backgroundColor: "#000",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
          position: "relative",
          cursor: clickToOpen ? "pointer" : "default",
        }}
        onClick={handlePdfClick}
      >
        <Worker
          workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
        >
          <Box sx={{ width: "100%", height: "100%" }}>
            <Viewer
              fileUrl={fileUrl}
              defaultScale={1}
              theme="dark"
              onDocumentLoad={handleDocumentLoadSuccess}
            />
          </Box>
        </Worker>

        {showNavigation && totalPages > 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "rgba(0, 0, 0, 0.7)",
              borderRadius: 2,
              px: 1,
              py: 0.5,
              zIndex: 10,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "white",
                minWidth: "80px",
                textAlign: "center",
                fontSize: "0.75rem",
              }}
            >
              {totalPages} {totalPages === 1 ? 'page' : 'pages'}
            </Typography>
          </Box>
        )}

        {clickToOpen && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0, 0, 0, 0.7)",
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenInNewTab(true);
                }}
                sx={{ color: "white" }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Dialog for opening in new tab */}
      <Dialog
        open={openInNewTab}
        onClose={() => setOpenInNewTab(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, minHeight: 600 }}>
          <Box sx={{ width: "100%", height: 600 }}>
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
            >
              <Viewer
                fileUrl={fileUrl}
                defaultScale={1.5}
                theme="light"
              />
            </Worker>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            startIcon={<Download />}
            onClick={handleDownload}
            variant="outlined"
          >
            Download PDF
          </Button>
          <Box>
            <Button onClick={() => setOpenInNewTab(false)} sx={{ mr: 1 }}>
              Close
            </Button>
            <Button
              onClick={handleOpenInNewTab}
              variant="contained"
              startIcon={<OpenInNew />}
            >
              Open in New Tab
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
